import React from 'react';
import { HelpCircle } from 'lucide-react';

/**
 * Les marques des placements Meta, dessinées plutôt qu'importées.
 *
 * Le dossier `logos/` ne contient ni Facebook ni Instagram en tant que réseaux — seulement
 * `meta-ads`, la marque de la régie, et `facebook-ads`, qui est son ancien logo. Des SVG en
 * ligne évitent d'ajouter deux binaires au dépôt pour deux pictogrammes de seize pixels, se
 * redimensionnent sans flou, et se colorent depuis le code.
 *
 * Les tracés suivent les marques officielles : le « f » de Facebook dans son disque bleu,
 * l'appareil photo d'Instagram et son dégradé. Un pictogramme approximatif serait pire que
 * l'émoji qu'il remplace.
 */

const LogoFacebook = ({ taille = 18 }) => (
  <svg width={taille} height={taille} viewBox="0 0 24 24" aria-label="Facebook" role="img">
    <circle cx="12" cy="12" r="12" fill="#1877F2" />
    <path
      fill="#FFFFFF"
      d="M16.67 15.47 17.2 12h-3.33V9.75c0-.95.47-1.88 1.96-1.88h1.51V4.92s-1.37-.23-2.68-.23c-2.73 0-4.52 1.66-4.52 4.65V12H7.08v3.47h3.06v8.39a12.14 12.14 0 0 0 3.73 0v-8.39h2.8Z"
    />
  </svg>
);

/**
 * Instagram : le dégradé est nommé par une clé unique.
 *
 * Deux `<linearGradient id="...">` identiques dans la même page se recouvrent, et toutes les
 * icônes prennent alors les couleurs de la dernière rendue. L'identifiant porte donc la
 * taille, seule chose qui varie d'un usage à l'autre ici.
 */
const LogoInstagram = ({ taille = 18 }) => {
  const idDegrade = `degradeInstagram${taille}`;
  return (
    <svg width={taille} height={taille} viewBox="0 0 24 24" aria-label="Instagram" role="img">
      <defs>
        <linearGradient id={idDegrade} x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#FFD521" />
          <stop offset="25%" stopColor="#F50000" />
          <stop offset="60%" stopColor="#B900B4" />
          <stop offset="100%" stopColor="#4C68D7" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="24" height="24" rx="6.5" fill={`url(#${idDegrade})`} />
      <rect x="5" y="5" width="14" height="14" rx="4.2" fill="none" stroke="#FFFFFF" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="3.4" fill="none" stroke="#FFFFFF" strokeWidth="1.8" />
      <circle cx="16.4" cy="7.6" r="1.1" fill="#FFFFFF" />
    </svg>
  );
};

const LogoMessenger = ({ taille = 18 }) => (
  <svg width={taille} height={taille} viewBox="0 0 24 24" aria-label="Messenger" role="img">
    <circle cx="12" cy="12" r="12" fill="#0084FF" />
    <path
      fill="#FFFFFF"
      d="M12 4.8c-4.2 0-7.2 3.07-7.2 7.03 0 2.26.98 4.24 2.58 5.5v2.87l2.37-1.3c.71.2 1.46.3 2.25.3 4.2 0 7.2-3.07 7.2-7.37S16.2 4.8 12 4.8Zm.73 9.44-1.86-1.98-3.6 1.98 3.96-4.2 1.9 1.98 3.56-1.98-3.96 4.2Z"
    />
  </svg>
);

const LogoAudienceNetwork = ({ taille = 18 }) => (
  <svg width={taille} height={taille} viewBox="0 0 24 24" aria-label="Audience Network" role="img">
    <circle cx="12" cy="12" r="12" fill="#0668E1" />
    <g fill="none" stroke="#FFFFFF" strokeWidth="1.6">
      <circle cx="12" cy="12" r="6.2" />
      <ellipse cx="12" cy="12" rx="2.8" ry="6.2" />
      <line x1="5.8" y1="12" x2="18.2" y2="12" />
    </g>
  </svg>
);

/**
 * Le pictogramme d'un placement, par son nom d'affichage.
 *
 * Un placement inconnu — ou « Non attribué », le panier de Meta pour l'activité qu'il ne
 * rattache à rien — reçoit un point d'interrogation plutôt qu'une marque au hasard : le
 * carton de l'émoji précédent laissait croire à une plateforme de plus.
 */
const LogoPlateforme = ({ plateforme, taille = 18 }) => {
  switch (plateforme) {
    case 'Facebook': return <LogoFacebook taille={taille} />;
    case 'Instagram': return <LogoInstagram taille={taille} />;
    case 'Messenger': return <LogoMessenger taille={taille} />;
    case 'Audience Network': return <LogoAudienceNetwork taille={taille} />;
    default:
      return <HelpCircle size={taille} style={{ color: '#9ca3af' }} aria-label={plateforme} />;
  }
};

export { LogoFacebook, LogoInstagram, LogoMessenger, LogoAudienceNetwork, LogoPlateforme };
export default LogoPlateforme;
